import { computed, watch } from 'vue';
import { useRoute, useRouter, useI18n } from '#imports';

export const useTermsLang = () => {
  const route = useRoute();
  const router = useRouter();
  const { locale, setLocale, locales } = useI18n();

  // Get supported locale codes dynamically from i18n config
  const supportedLocales = computed(() => {
    if (!locales.value) return ['en', 'kh'];
    return locales.value.map((l) => (typeof l === 'string' ? l : l.code));
  });

  // Normalize and validate the language code
  const getValidLang = (langStr: unknown): string => {
    if (typeof langStr !== 'string') return 'en'; // default fallback
    const normalized = langStr.toLowerCase();

    // Map common Khmer ISO/alias codes to the local files' code ('kh')
    if (normalized === 'km' || normalized === 'kh') {
      return 'kh';
    }

    // Check if the language is supported in i18n configuration
    if (supportedLocales.value.includes(normalized)) {
      return normalized;
    }

    return 'en'; // default fallback
  };

  // 1. Sync from URL Query to i18n locale on load/change of route query
  watch(
    () => route.query.lang,
    (newLang) => {
      const validLang = getValidLang(newLang);
      if (locale.value !== validLang) {
        setLocale(validLang as typeof locale.value);
      }
    },
    { immediate: true }
  );

  // 2. Sync from i18n locale change (e.g. Header switch) back to URL Query
  watch(locale, (newLocale) => {
    const currentQueryLang = route.query.lang;
    const targetLang = getValidLang(newLocale);
    if (currentQueryLang !== targetLang) {
      router.replace({
        query: {
          ...route.query,
          lang: targetLang,
        },
      });
    }
  });

  // Reactive value of the validated active language
  const currentLang = computed(() => {
    return getValidLang(route.query.lang);
  });

  return {
    currentLang,
  };
};
